import { LineItem } from "@/types/db/invoices";

/** Round to nearest cent */
const round2 = (n: number) => Math.round(n * 100) / 100;

export interface CombinedLineItems {
  /** Line items with any combined admin-fee / discount portion stripped back to base. */
  items: LineItem[];
  /** Sum of the combined admin fee (0 when not combining). */
  adminFeeTotal: number;
  /** Sum of the combined discount (0 when not combining). */
  discountTotal: number;
}

/**
 * Transforms line items when "Combine Admin Fee" / "Combine Discount" is checked.
 * Strips the per-item fee/discount back out of each amount (to base) and returns
 * the combined totals so the caller can surface them in the footer — subtotal =
 * Σ of the returned item amounts, then + adminFeeTotal − discountTotal = total.
 * When neither is combined, items pass through untouched and totals are 0.
 */
export function combineLineItems(
  items: LineItem[],
  combineAdminFee: boolean,
  combineDiscount: boolean,
): CombinedLineItems {
  if (!combineAdminFee && !combineDiscount) {
    return { items, adminFeeTotal: 0, discountTotal: 0 };
  }

  let totalAdminFee = 0;
  let totalDiscount = 0;

  const transformed: LineItem[] = items.map((item) => {
    const baseAmount = round2(item.unit_price * item.quantity);
    const itemAdminFee = item.admin_fee ? round2((baseAmount * item.admin_fee) / 100) : 0;
    const itemDiscount = item.discount ? round2((baseAmount * item.discount) / 100) : 0;

    let newAmount = item.amount;
    let newAdminFee = item.admin_fee;
    let newDiscount = item.discount;

    if (combineAdminFee && item.admin_fee) {
      totalAdminFee = round2(totalAdminFee + itemAdminFee);
      newAmount = round2(newAmount - itemAdminFee);
      newAdminFee = undefined;
    }

    if (combineDiscount && item.discount) {
      totalDiscount = round2(totalDiscount + itemDiscount);
      newAmount = round2(newAmount + itemDiscount);
      newDiscount = undefined;
    }

    return {
      ...item,
      amount: newAmount,
      admin_fee: newAdminFee,
      discount: newDiscount,
    };
  });

  return { items: transformed, adminFeeTotal: totalAdminFee, discountTotal: totalDiscount };
}
